// Phase 2 · Footprint endpoints. PolarisApi extends this class, so callers keep
// using polarisApi.getFootprint() etc.; split out of index.ts to keep that file
// within the repo's per-file size budget.
import HttpClient from "api/index";
import {
	TFootprintResponse,
	TGenerateClusterRequest,
	TGenerateClusterResponse,
	TIncludeScope,
} from "api/polarisApi/types";

// Writing and filling a cluster takes ~15-30 s server-side (an LLM draft plus a
// catalog search); allow well beyond that before giving up.
export const GENERATE_CLUSTER_TIMEOUT_MS = 120_000;

class PolarisFootprintApi extends HttpClient {
	async getFootprint(searchId: string, requestConfig = {}) {
		return this.doGet<TFootprintResponse>(
			`/polaris/searches/${searchId}/footprint`,
			requestConfig,
		);
	}

	async patchFootprint(searchId: string, includeScope: TIncludeScope, requestConfig = {}) {
		return this.doPatch<TFootprintResponse, {include_scope: TIncludeScope}>(
			`/polaris/searches/${searchId}/footprint`,
			{include_scope: includeScope},
			requestConfig,
		);
	}

	async deleteCluster(searchId: string, clusterId: string, requestConfig = {}) {
		return this.doDelete<TFootprintResponse, Record<string, never>>(
			`/polaris/searches/${searchId}/footprint/clusters/${clusterId}`,
			undefined,
			requestConfig,
		);
	}

	// "Add cluster": Polaris drafts a cluster from the description and finds its
	// companies. Answers 201 with the footprint state plus added_cluster_id; 409
	// when the map is not editable or every company found is already on it; 503
	// when nothing could be built (nothing is written then).
	async generateCluster(searchId: string, description: string, requestConfig = {}) {
		return this.doPost<TGenerateClusterResponse, TGenerateClusterRequest>(
			`/polaris/searches/${searchId}/footprint/clusters/generate`,
			{description},
			{timeout: GENERATE_CLUSTER_TIMEOUT_MS, ...requestConfig},
		);
	}

	async deleteClusterCompany(
		searchId: string,
		clusterId: string,
		companyName: string,
		requestConfig = {},
	) {
		return this.doDelete<TFootprintResponse, Record<string, never>>(
			`/polaris/searches/${searchId}/footprint/clusters/${clusterId}/companies/${encodeURIComponent(
				companyName,
			)}`,
			undefined,
			requestConfig,
		);
	}

	// Re-runs a failed progressive generation. Allowed only when the footprint
	// says can_retry; answers with the same body as GET (normally pending, no
	// clusters), otherwise 409 footprint_not_retryable.
	async retryFootprint(searchId: string, requestConfig = {}) {
		return this.doPost<TFootprintResponse, Record<string, never>>(
			`/polaris/searches/${searchId}/footprint/retry`,
			{},
			requestConfig,
		);
	}
}

export default PolarisFootprintApi;
